import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, TextField, Grid, Avatar, Alert
} from '@mui/material';
import { Edit, Save } from '@mui/icons-material';
import api from '../api';
import { useSchool } from '../contexts/SchoolContext';

interface SchoolProfileFormData {
  name: string;
  email: string;
  address: string;
  phone: string;
  description: string;
  activeTermName: string;
  activeTermStartDate: string;
  activeTermEndDate: string;
  termDurationMonths: string;
  marksSubmissionDeadline: string;
  logoUrl: string;
}

const getMonthsBetween = (start: Date, end: Date) => {
  const years = end.getFullYear() - start.getFullYear();
  let total = years * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() >= start.getDate()) {
    total += 1;
  }
  return total > 0 ? total : undefined;
};

const SchoolProfile: React.FC = () => {
  const { school, updateSchoolProfile, loading } = useSchool();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoSource, setLogoSource] = useState<'url' | 'upload'>('url');
  const [logoPreview, setLogoPreview] = useState<string>('');

  const logoUploadToDataUri = (logoUpload?: { data?: string | { type?: string; data?: number[] }; contentType?: string }) => {
    if (!logoUpload?.data || !logoUpload.contentType) return '';

    if (typeof logoUpload.data === 'string') {
      return `data:${logoUpload.contentType};base64,${logoUpload.data}`;
    }

    if (typeof logoUpload.data === 'object' && Array.isArray(logoUpload.data.data)) {
      try {
        const bytes = Uint8Array.from(logoUpload.data.data);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        return `data:${logoUpload.contentType};base64,${window.btoa(binary)}`;
      } catch {
        return '';
      }
    }

    return '';
  };

  const [formData, setFormData] = useState<SchoolProfileFormData>({
    name: school?.name || '',
    email: school?.email || '',
    address: school?.address || '',
    phone: school?.phone || '',
    description: school?.description || '',
    activeTermName: school?.activeTermName || '',
    activeTermStartDate: school?.activeTermStartDate ? new Date(school.activeTermStartDate).toISOString().slice(0, 10) : '',
    activeTermEndDate: school?.activeTermEndDate ? new Date(school.activeTermEndDate).toISOString().slice(0, 10) : '',
    termDurationMonths: school?.termDurationMonths?.toString() || '',
    marksSubmissionDeadline: school?.marksSubmissionDeadline ? new Date(school.marksSubmissionDeadline).toISOString().slice(0, 10) : '',
    logoUrl: school?.logoUrl || ''
  });

  useEffect(() => {
    setFormData({
      name: school?.name || '',
      email: school?.email || '',
      address: school?.address || '',
      phone: school?.phone || '',
      description: school?.description || '',
      activeTermName: school?.activeTermName || '',
      activeTermStartDate: school?.activeTermStartDate ? new Date(school.activeTermStartDate).toISOString().slice(0, 10) : '',
      activeTermEndDate: school?.activeTermEndDate ? new Date(school.activeTermEndDate).toISOString().slice(0, 10) : '',
      termDurationMonths: school?.termDurationMonths?.toString() || '',
      marksSubmissionDeadline: school?.marksSubmissionDeadline ? new Date(school.marksSubmissionDeadline).toISOString().slice(0, 10) : '',
      logoUrl: school?.logoUrl || ''
    });
    setLogoFile(null);
    setLogoSource(school?.logoUrl ? 'url' : 'upload');
    if (school?.logoUrl) {
      setLogoPreview(school.logoUrl);
    } else {
      setLogoPreview(logoUploadToDataUri(school?.logoUpload));
    }
  }, [school]);

  useEffect(() => {
    if (!editing) return;
    if (!formData.activeTermStartDate || !formData.activeTermEndDate) return;

    const start = new Date(formData.activeTermStartDate);
    const end = new Date(formData.activeTermEndDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return;

    const months = getMonthsBetween(start, end);
    if (months !== undefined && formData.termDurationMonths !== months.toString()) {
      setFormData((prev) => ({ ...prev, termDurationMonths: months.toString() }));
    }
  }, [editing, formData.activeTermStartDate, formData.activeTermEndDate, formData.termDurationMonths]);

  const handleEdit = () => {
    setEditing(true);
  };

  const uploadLogoFileToDb = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/uploads/image-blob', formData, {
      headers: {
        'x-auth-token': localStorage.getItem('school_token') || ''
      }
    });
    return response.data.data;
  };

  const validateForm = () => {
    setSaveError(null);
    let start: Date | null = null;
    let end: Date | null = null;

    if (formData.activeTermStartDate) {
      start = new Date(formData.activeTermStartDate);
      if (formData.activeTermEndDate) {
        end = new Date(formData.activeTermEndDate);
      } else if (formData.termDurationMonths) {
        end = new Date(formData.activeTermStartDate);
        end.setMonth(end.getMonth() + Number(formData.termDurationMonths));
      }
    }

    if (start && end && end < start) {
      setSaveError('Term end date must be on or after the term start date.');
      return false;
    }

    if (formData.marksSubmissionDeadline && start && end) {
      const deadline = new Date(formData.marksSubmissionDeadline);
      if (deadline < start || deadline > end) {
        setSaveError('Submission deadline must fall within the active term period.');
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    setSaveError(null);

    try {
      let logoUploadData;
      let logoUrlData = formData.logoUrl || undefined;

      if (logoSource === 'upload' && logoFile) {
        logoUploadData = await uploadLogoFileToDb(logoFile);
        logoUrlData = undefined;
      }

      await updateSchoolProfile({
        name: formData.name,
        email: formData.email,
        address: formData.address,
        phone: formData.phone,
        description: formData.description,
        activeTermName: formData.activeTermName,
        activeTermStartDate: formData.activeTermStartDate || undefined,
        activeTermEndDate: formData.activeTermEndDate || undefined,
        termDurationMonths: formData.termDurationMonths ? Number(formData.termDurationMonths) : undefined,
        marksSubmissionDeadline: formData.marksSubmissionDeadline || undefined,
        logoUrl: logoUrlData,
        logoUpload: logoUploadData
      });
      setLogoFile(null);
      setEditing(false);
    } catch (error: any) {
      setSaveError(error.response?.data?.message || error.message || 'Unable to update profile.');
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: school?.name || '',
      email: school?.email || '',
      address: school?.address || '',
      phone: school?.phone || '',
      description: school?.description || '',
      activeTermName: school?.activeTermName || '',
      activeTermStartDate: school?.activeTermStartDate ? new Date(school.activeTermStartDate).toISOString().slice(0, 10) : '',
      activeTermEndDate: school?.activeTermEndDate ? new Date(school.activeTermEndDate).toISOString().slice(0, 10) : '',
      termDurationMonths: school?.termDurationMonths?.toString() || '',
      marksSubmissionDeadline: school?.marksSubmissionDeadline ? new Date(school.marksSubmissionDeadline).toISOString().slice(0, 10) : '',
      logoUrl: school?.logoUrl || ''
    });
    setLogoFile(null);
    setLogoSource(school?.logoUrl ? 'url' : 'upload');
    setLogoPreview(school?.logoUrl || logoUploadToDataUri(school?.logoUpload));
    setSaveError(null);
    setEditing(false);
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          School Profile
        </Typography>
        {!editing ? (
          <Button variant="outlined" startIcon={<Edit />} onClick={handleEdit}>
            Edit Profile
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 4 }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {saveError}
          </Alert>
        )}

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                src={logoPreview || undefined}
                sx={{ width: 120, height: 120, mb: 2, bgcolor: 'primary.main' }}
              >
                {school?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {school?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {school?.email}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="School Name"
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Address"
                  fullWidth
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Active Term Name"
                  fullWidth
                  value={formData.activeTermName}
                  onChange={(e) => setFormData(prev => ({ ...prev, activeTermName: e.target.value }))}
                  disabled={!editing}
                  helperText="Example: First Term 2026/2027"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Term Duration (months)"
                  type="number"
                  fullWidth
                  value={formData.termDurationMonths}
                  onChange={(e) => setFormData(prev => ({ ...prev, termDurationMonths: e.target.value }))}
                  disabled={!editing}
                  InputProps={{ inputProps: { min: 1 } }}
                  helperText="Duration of the active term. Leave empty to disable fixed term window."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Term Start Date"
                  type="date"
                  fullWidth
                  value={formData.activeTermStartDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, activeTermStartDate: e.target.value }))}
                  disabled={!editing}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Term End Date"
                  type="date"
                  fullWidth
                  value={formData.activeTermEndDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, activeTermEndDate: e.target.value }))}
                  disabled={!editing}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Marks Submission Deadline"
                  type="date"
                  fullWidth
                  value={formData.marksSubmissionDeadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, marksSubmissionDeadline: e.target.value }))}
                  disabled={!editing}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Logo URL"
                  fullWidth
                  value={formData.logoUrl}
                  onChange={(e) => {
                    setLogoSource('url');
                    setLogoFile(null);
                    setFormData(prev => ({ ...prev, logoUrl: e.target.value }));
                    setLogoPreview(e.target.value);
                  }}
                  disabled={!editing}
                  helperText="Provide a logo URL or upload a file below."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={!editing}
                >
                  Upload Logo File
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      if (!file) return;
                      setLogoFile(file);
                      setLogoSource('upload');
                      setLogoPreview(URL.createObjectURL(file));
                    }}
                  />
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={!editing}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SchoolProfile;